import React from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQButton from '../../components/button';
import UQLoader from '../../components/loader';
import UQCard from '../../components/card';
import UQSpacer from '../../components/spacer';
import UQGrid from '../../components/grid';
import UQRow from '../../components/row';
import UQCol from '../../components/col';
import UQIcon from '../../components/icon';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';
import { BREADCRUMB_ROOT } from '../../common/config';

const ComponentsPage = () => {
  const attributes = [
    {
      attribute: 'type',
      type: "'primary' | 'secondary' | 'action'",
      default: "'primary'",
      description: 'The style of the button.',
    },
    {
      attribute: 'size',
      type: "'small' | 'large'",
      default: 'undefined',
      description: 'The size of the button.',
    },
    {
      attribute: 'icon',
      type: 'string',
      default: 'undefined',
      description: 'The icon to display inside the button.',
    },
    {
      attribute: 'iconPosition',
      type: "'left' | 'right'",
      default: "'left'",
      description: 'The position of the icon relative to the text.',
    },
    {
      attribute: 'loading',
      type: 'boolean',
      default: 'false',
      description: 'Whether the loader is in a loading state.',
    },
    {
      attribute: 'header',
      type: 'string',
      default: 'undefined',
      description: 'The header text for the card.',
    },
    {
      attribute: 'iconColor',
      type: 'string',
      default: 'undefined',
      description: 'The color of the icon in the card.',
    },
  ];

  const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
    {
      columnId: 'attribute',
      renderHeaderCell: () => 'Attribute',
      renderCell: (item) => item.attribute,
    },
    {
      columnId: 'type',
      renderHeaderCell: () => 'Type',
      renderCell: (item) => <code>{item.type}</code>,
    },
    {
      columnId: 'default',
      renderHeaderCell: () => 'Default',
      renderCell: (item) => <code>{item.default}</code>,
    },
    {
      columnId: 'description',
      renderHeaderCell: () => 'Description',
      renderCell: (item) => item.description,
    },
  ];

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Components']} />
      <h1>Components Overview</h1>
      <UQSpacer size="m" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        This page provides an overview of the components available in the <code>@uniqconsulting/ui</code> library. Each component is showcased with examples and descriptions.
      </p>
      <UQSpacer size="m" />

      {/* Attributes Table */}
      <h2>Attributes</h2>
      <UQSpacer size="s" />
      <UQDataGrid
        items={attributes}
        columns={attributeColumns}
        sortable={false}
        selectionMode="none"
      />
      <UQSpacer size="m" />

      {/* Buttons Section */}
      <h2>Buttons</h2>
      <UQSpacer size="s" />
      <UQGrid gap="1rem" justify="start">
        <UQButton type="primary" size="large" icon="plane" iconPosition="left">
          Primary Button
        </UQButton>
        <UQButton type="secondary" size="large" icon="check" iconPosition="left">
          Secondary Button
        </UQButton>
        <UQButton type="action" size="large" icon="edit" iconPosition="left">
          Action Button
        </UQButton>
      </UQGrid>
      <UQSpacer size="m" />

      {/* Loader Section */}
      <h2>Loader</h2>
      <UQSpacer size="s" />
      <div style={{ textAlign: 'center' }}>
        <UQLoader />
      </div>
      <UQSpacer size="m" />

      {/* Cards Section */}
      <h2>Cards</h2>
      <UQSpacer size="s" />
      <UQGrid gap="1rem" justify="start">
        <UQCard header="Card Header" icon="info" iconColor="primary">
          <p>This is a simple card with a header and content.</p>
        </UQCard>
        <UQCard header="Loading Card" loading>
          <p>This card is in a loading state.</p>
        </UQCard>
      </UQGrid>
      <UQSpacer size="m" />

      {/* Icons Section */}
      <h2>Icons</h2>
      <UQSpacer size="s" />
      <UQRow>
        <UQCol span={2}>
          <UQIcon icon="bell-ringing-04" />
          <p>bell-ringing-04</p>
        </UQCol>
        <UQCol span={2}>
          <UQIcon icon="check" />
          <p>Check</p>
        </UQCol>
        <UQCol span={2}>
          <UQIcon icon="edit" />
          <p>Edit</p>
        </UQCol>
      </UQRow>
      <UQSpacer size="m" />
    </>
  );
};

export default ComponentsPage;